import React from "react";
import { Box, Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { FiCheck } from "react-icons/fi";
import { motion, useReducedMotion } from "framer-motion";
import { alpha, useOppTheme } from "./useOppTheme";

export interface OppStep { key: string; label: string; hint?: string }

/**
 * A row of steps (a column on a phone) with the finished ones ticked, the current one lit, and the rest
 * waiting. `done` is how many steps are complete, counted from the start — it always comes from real
 * state, never from effort. `muted` greys the whole thing (e.g. cancelled work). Added 2026-09-12 for the
 * work loop and the talk-it-through drafter. Readable for every age: labels 14–16px, never tiny capitals,
 * and the step's words are on the page (the hint is text, not a tooltip).
 */
export function OppStepper({ steps, done, muted = false, size = "md" }: { steps: OppStep[]; done: number; muted?: boolean; size?: "sm" | "md" }) {
  const t = useOppTheme();
  const reduce = useReducedMotion();
  const dot = size === "sm" ? 8 : 10;
  return (
    <Stack direction={{ base: "column", sm: "row" }} spacing={{ base: 2, sm: 0 }} align={{ base: "stretch", sm: "flex-start" }} w="100%" opacity={muted ? 0.55 : 1}
      role="list" aria-label={`${Math.min(done, steps.length)} of ${steps.length} steps done`}>
      {steps.map((s, i) => {
        const state = i < done ? "done" : i === done && !muted ? "current" : "todo";
        const colour = state === "done" ? t.success : state === "current" ? t.primary : t.neutral;
        return (
          <Flex key={s.key} role="listitem" flex={1} direction={{ base: "row", sm: "column" }} align={{ base: "center", sm: "flex-start" }} gap={{ base: 3, sm: 2 }} minW={0} position="relative"
            aria-current={state === "current" ? "step" : undefined}>
            <HStack spacing={0} w={{ sm: "100%" }} align="center">
              <motion.div initial={reduce ? false : { scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: reduce ? 0 : i * 0.08, type: "spring", stiffness: 480, damping: 26 }}>
                <Flex w={dot} h={dot} borderRadius="full" align="center" justify="center" flexShrink={0}
                  bg={state === "done" ? t.success : state === "current" ? alpha(t.primary, 0.14) : "transparent"}
                  borderWidth={state === "done" ? 0 : "2px"} borderColor={colour}
                  boxShadow={state === "current" ? `0 0 0 4px ${alpha(t.primary, 0.15)}` : undefined}>
                  {state === "done"
                    ? <Icon as={FiCheck} color="white" boxSize={size === "sm" ? 4 : 5} />
                    : <Text fontSize={size === "sm" ? "sm" : "md"} fontWeight="bold" color={colour}>{i + 1}</Text>}
                </Flex>
              </motion.div>
              {i < steps.length - 1 && (
                <Box display={{ base: "none", sm: "block" }} flex={1} h="2px" mx={2} borderRadius="full" bg={alpha(t.neutral, 0.25)} overflow="hidden">
                  <motion.div style={{ height: "100%", background: t.success }} initial={{ width: reduce && i + 1 <= done ? "100%" : "0%" }}
                    animate={{ width: i + 1 <= done ? "100%" : "0%" }} transition={{ duration: reduce ? 0 : 0.6, delay: reduce ? 0 : i * 0.08 + 0.1 }} />
                </Box>
              )}
            </HStack>
            <Box minW={0} pr={{ sm: 3 }}>
              <Text fontSize={size === "sm" ? "sm" : "md"} fontWeight={state === "current" ? "bold" : "semibold"} color={state === "todo" ? t.muted : t.text}>{s.label}</Text>
              {s.hint && <Text fontSize="sm" color={t.faint}>{s.hint}</Text>}
            </Box>
          </Flex>
        );
      })}
    </Stack>
  );
}
